import {
  useQuery,
  type QueryHookOptions,
  useMutation,
  type MutationHookOptions,
  useLazyQuery,
} from "@apollo/client";
import { merge } from "lodash";
import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";

import { checkStatusMessage } from "../../formats/checkStatus";
import { formatEmployeeName } from "../../formats/employees";
import {
  FinanceCheckSourceType,
  type ChecksQuery,
  type ChecksQueryVariables,
  ChecksDocument,
  PayrollChecksCreateDocument,
  LastCheckNumberDocument,
  VoidCheckDocument,
  type VoidCheckMutation,
  type VoidCheckMutationVariables,
  type FinanceCheckStatusQuery,
  type FinanceCheckStatusQueryVariables,
  FinanceCheckStatusDocument,
  FinanceCheckCreateDocument,
  type FinanceCheckCreateMutation,
  type FinanceCheckCreateMutationVariables,
  EmployeeOptionsDocument,
  type EmployeeOptionsQuery,
  type EmployeeOptionsQueryVariables,
  CounterpartiesDocument,
  type CounterpartiesQuery,
  type CounterpartiesQueryVariables,
  SortDirection,
} from "../../graphql";

export function useChecks(
  options?: QueryHookOptions<ChecksQuery, ChecksQueryVariables>
) {
  const { loading, data } = useQuery(ChecksDocument, options);

  const items = data?.financeChecks?.items;
  const totalCount = data?.financeChecks?.totalCount;

  return { loading, items, totalCount };
}

export function useCheck(id?: string) {
  const { loading, data } = useQuery(ChecksDocument, {
    variables: { filter: { id: id ? [id] : [] }, pageSize: 1 },
    skip: !id,
  });

  return { loading, check: data?.financeChecks?.items?.[0] };
}

export function useLastCheckNumber(bankAccountId?: string) {
  const { loading, data } = useQuery(LastCheckNumberDocument, {
    variables: { bankAccountId },
    skip: !bankAccountId,
    fetchPolicy: "network-only",
  });

  return {
    loading,
    number: data?.financeChecks?.items?.[0]?.checkNumber,
  };
}

function useCheckStatusFormat() {
  const intl = useIntl();
  return (status: string) => {
    const statusMessage = checkStatusMessage(status);
    return typeof statusMessage === "string"
      ? statusMessage
      : intl.formatMessage(statusMessage);
  };
}

export function useCheckStatusOptions() {
  const [load, { loading, data }] = useLazyQuery<
    FinanceCheckStatusQuery,
    FinanceCheckStatusQueryVariables
  >(FinanceCheckStatusDocument);

  const formatCheckStatus = useCheckStatusFormat();

  const options = data?.__type?.enumValues
    ?.map((s) => ({
      key: s.name,
      value: s.name,
      label: formatCheckStatus(s.name),
    }))
    ?.toSorted((a, b) => a.label.localeCompare(b.label));

  return {
    load,
    loading,
    options,
  };
}

export function useFinanceCheckSourceTypeOptions() {
  const intl = useIntl();

  const options = useMemo(
    () => [
      {
        key: FinanceCheckSourceType.PayrollEntry,
        value: FinanceCheckSourceType.PayrollEntry,
        label: intl.formatMessage({
          id: "payrollEntries.entityName",
          defaultMessage: "Payroll Entry",
        }),
      },
      {
        key: FinanceCheckSourceType.FinanceInvoice,
        value: FinanceCheckSourceType.FinanceInvoice,
        label: intl.formatMessage({
          id: "invoices.entityName",
          defaultMessage: "Invoice",
        }),
      },
    ],
    [intl]
  );

  return { options };
}

export function usePayrollChecksCreate() {
  return useMutation(PayrollChecksCreateDocument);
}

export function useFinanceCheckCreate(
  options?: MutationHookOptions<
    FinanceCheckCreateMutation,
    FinanceCheckCreateMutationVariables
  >
) {
  return useMutation(FinanceCheckCreateDocument, options);
}

export function useCheckVoid(
  options?: MutationHookOptions<VoidCheckMutation, VoidCheckMutationVariables>
) {
  return useMutation(VoidCheckDocument, options);
}

export function usePayeeOptions() {
  const intl = useIntl();

  const [loadEmployees, { loading: loadingEmployees, data: employeeData }] =
    useLazyQuery<EmployeeOptionsQuery, EmployeeOptionsQueryVariables>(
      EmployeeOptionsDocument,
      { variables: { sort: { firstName: SortDirection.Asc } } }
    );

  const [
    loadCounterparties,
    { loading: loadingCounterparties, data: counterpartyData },
  ] = useLazyQuery<CounterpartiesQuery, CounterpartiesQueryVariables>(
    CounterpartiesDocument,
    { variables: { sort: { name: SortDirection.Asc } } }
  );

  const employeesLabel = intl.formatMessage({
    id: "employees",
    defaultMessage: "Employees",
  });
  const counterpartiesLabel = intl.formatMessage({
    id: "counterparties.entityName",
    defaultMessage: "Counterparties",
  });

  const options = [
    ...(employeeData?.employees?.items ?? []).map((e) => ({
      key: `Employee/${e.id}`,
      label: formatEmployeeName(e),
      group: employeesLabel,
    })),
    ...(counterpartyData?.counterparties?.items ?? []).map((c) => ({
      key: `Counterparty/${c.id}`,
      label: c.name,
      group: counterpartiesLabel,
    })),
  ];

  const load = useCallback(() => {
    loadEmployees();
    loadCounterparties();
  }, [loadEmployees, loadCounterparties]);

  const search = useCallback(
    (val: string | undefined) => {
      loadEmployees(
        merge(
          {},
          { variables: { filter: val ? { fullName: val } : undefined } }
        )
      );
      loadCounterparties(
        merge(
          {},
          {
            variables: {
              sort: { name: SortDirection.Asc },
              filter: val ? { name: { match: val } } : undefined,
            },
          }
        )
      );
    },
    [loadEmployees, loadCounterparties]
  );

  return {
    load,
    search,
    loading: loadingEmployees || loadingCounterparties,
    options,
  };
}
